import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Star, User } from 'lucide-react';
import { trackButtonClick } from '../utils/tracking';

const MAX_COMMENT_LENGTH = 1000;
const MIN_SUBMIT_MS = 2000;

const inputClassName =
  'w-full px-3 py-2.5 rounded-xl border border-slate-200 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300';

function RatingStar({ active, onClick, label }) {
  return (
    <button
      type="button"
      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center border transition-colors ${
        active
          ? 'bg-amber-50 border-amber-200 text-amber-500'
          : 'bg-white border-slate-200 text-slate-300'
      } hover:border-amber-300 hover:text-amber-500`}
      onClick={onClick}
      aria-label={label}
    >
      <Star
        className="w-5 h-5 sm:w-6 sm:h-6"
        fill={active ? 'currentColor' : 'none'}
        strokeWidth={active ? 1.5 : 2}
      />
    </button>
  );
}

export default function FeedbackModal({
  open,
  onClose,
  sessionId,
  userEmail,
}) {
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState('');
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const openedAtRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const bodyScrollRef = useRef(null);
  const commentsRef = useRef(null);
  const nameInputRef = useRef(null);
  const [visibleViewport, setVisibleViewport] = useState(null);
  onCloseRef.current = onClose;

  const scrollFieldIntoView = (element) => {
    if (!element) return;
    window.requestAnimationFrame(() => {
      setTimeout(() => {
        const vv = window.visualViewport;
        const visibleBottom = (vv?.height ?? window.innerHeight) + (vv?.offsetTop ?? 0) - 16;
        const fieldRect = element.getBoundingClientRect();
        const scrollParent = bodyScrollRef.current;

        if (fieldRect.bottom > visibleBottom && scrollParent) {
          scrollParent.scrollTop += fieldRect.bottom - visibleBottom + 12;
        }
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }, 320);
    });
  };

  useEffect(() => {
    if (!open) {
      setVisibleViewport(null);
      return undefined;
    }

    const vv = window.visualViewport;
    if (!vv) return undefined;

    const syncViewport = () => {
      setVisibleViewport({
        top: vv.offsetTop,
        left: vv.offsetLeft,
        width: vv.width,
        height: vv.height,
      });
    };

    syncViewport();
    vv.addEventListener('resize', syncViewport);
    vv.addEventListener('scroll', syncViewport);

    return () => {
      vv.removeEventListener('resize', syncViewport);
      vv.removeEventListener('scroll', syncViewport);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    openedAtRef.current = new Date().toISOString();
    setName('');
    setRating(0);
    setComments('');
    setWebsite('');
    setError('');
    setSubmitted(false);
    setSubmitting(false);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (submitting) return;
    if (rating < 1 || rating > 5) {
      setError('Please choose a star rating before submitting.');
      return;
    }
    const openedMs = Date.parse(openedAtRef.current || '');
    if (!Number.isFinite(openedMs) || Date.now() - openedMs < MIN_SUBMIT_MS) {
      setError('Please wait a moment before submitting.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      trackButtonClick('submit_feedback', { page: 'avatar', rating });
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId || null,
          name: name.trim() || userEmail || '',
          rating,
          comments,
          website,
          openedAt: openedAtRef.current,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || `Failed (${res.status})`);
        return;
      }
      setSubmitted(true);
    } catch (e) {
      setError(e?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const shellStyle = visibleViewport
    ? {
        top: visibleViewport.top,
        left: visibleViewport.left,
        width: visibleViewport.width,
        height: visibleViewport.height,
      }
    : undefined;

  const keyboardOpen = visibleViewport
    && visibleViewport.height < window.innerHeight * 0.85;

  return createPortal(
    <div
      className={`fixed z-[10050] flex touch-auto overflow-hidden overscroll-contain ${
        shellStyle ? '' : 'inset-0'
      } ${keyboardOpen ? 'items-start justify-center' : 'items-center justify-center'}`}
      style={{
        ...shellStyle,
        padding: keyboardOpen
          ? '0.5rem 0.75rem 0.5rem'
          : 'max(0.75rem, env(safe-area-inset-top)) max(0.75rem, env(safe-area-inset-right)) max(0.75rem, env(safe-area-inset-bottom)) max(0.75rem, env(safe-area-inset-left))',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-modal-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        aria-label="Close feedback form"
        onClick={onClose}
      />

      <div
        className="relative z-[1] w-full max-w-[min(100%,22rem)] sm:max-w-xl flex flex-col bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-auto min-h-0"
        style={{
          maxHeight: visibleViewport
            ? `min(${visibleViewport.height - 16}px, 88dvh)`
            : 'min(88dvh, calc(100dvh - 1.5rem))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 sm:px-6 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-slate-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 pr-2">
              <p className="text-sm font-semibold text-slate-500">Feedback</p>
              <h2 id="feedback-modal-title" className="mt-1 text-lg sm:text-2xl font-bold text-[#1a2133] leading-snug">
                How was your session?
              </h2>
              <p className="mt-1 text-sm text-slate-500 hidden sm:block">
                Your feedback helps us improve your experience.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              aria-label="Close feedback form"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body — scrolls on small screens */}
        <div
          ref={bodyScrollRef}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6"
        >
          <input
            type="text"
            name="company_fax"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
          />

          {submitted ? (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl px-4 py-3 text-sm">
              Thanks — your feedback was submitted.
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-rose-50 border border-rose-200 text-rose-900 rounded-xl px-4 py-3 text-sm" role="alert" aria-live="assertive">
                  {error}
                </div>
              )}

              <div>
                <div className="mb-2 sm:mb-3 text-base font-bold text-[#1a2133]">Star rating</div>
                {rating === 0 && (
                  <p className="text-sm text-slate-500 mb-2">Please choose a rating.</p>
                )}
                <div className="flex items-start justify-center sm:justify-start gap-1 sm:gap-3 flex-wrap">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <div key={n} className="flex flex-col items-center gap-1 sm:gap-2">
                      <RatingStar
                        active={rating >= n}
                        onClick={() => setRating(n)}
                        label={`${n} star`}
                      />
                      {n === 1 && <span className="text-sm text-slate-500">Poor</span>}
                      {n === 5 && <span className="text-sm text-slate-500">Excellent</span>}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block mb-2 text-base font-bold text-[#1a2133]">
                  Name <span className="text-sm text-slate-400 font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <User className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onFocus={() => scrollFieldIntoView(nameInputRef.current)}
                    className={`${inputClassName} pl-10`}
                    placeholder="Your name"
                  />
                </div>
              </div>

              <div>
                <label className="block mb-2 text-base font-bold text-[#1a2133]">
                  Comments <span className="text-sm text-slate-400 font-normal">(optional)</span>
                </label>
                <textarea
                  ref={commentsRef}
                  value={comments}
                  onChange={(e) => setComments(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
                  onFocus={() => scrollFieldIntoView(commentsRef.current)}
                  maxLength={MAX_COMMENT_LENGTH}
                  className={`${inputClassName} min-h-[96px] sm:min-h-[140px] resize-none`}
                  placeholder="Tell us what went well or how we can improve…"
                />
                <p className="mt-2 text-sm text-slate-500 text-right">
                  {comments.length} / {MAX_COMMENT_LENGTH}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer — always visible */}
        <div className="flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200 bg-white flex flex-row items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
          >
            {submitted ? 'Close' : 'Not now'}
          </button>
          {!submitted && (
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="min-h-[44px] px-6 py-2.5 rounded-xl bg-sky-400 text-white font-semibold hover:bg-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
